import { useEffect, useState } from "react";
import { Icon } from "../components/icons";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { acceptInvite, api, completeGoogle, login, logout, resendCode, startGoogle, verifyCode, type CodeChallenge, completeSupport } from "../lib/api";
import { useMe, useSession } from "../lib/session";
import { roleLabel } from "../lib/format";
import { ErrorText, Field, Form, Loading, str } from "../components/ui";
import { AccountLeaving } from "../components/Layout";

// Why a Google sign-in came back without signing in (?google_error=…).
const googleErrors: Record<string, string> = {
  no_account: "There's no SVENEX account for that Google email. Ask your administrator for an invite, then accept it with Google.",
  email_mismatch: "That Google account's email doesn't match this invite. Choose the Google account for the invited email.",
  unverified: "Google hasn't verified that email address yet.",
  operator: "Operator accounts sign in with a password and email code.",
  company_blocked: "Your company asks you to sign in with your email and password instead of Google.",
  disabled: "This account is disabled. Contact your administrator.",
  invite_invalid: "This invite link is invalid or has expired.",
  expired: "The Google sign-in took too long or was started in another tab. Please try again.",
  cancelled: "Google sign-in was cancelled.",
  rate_limited: "Too many attempts. Please wait a few minutes and try again.",
  failed: "Google sign-in didn't work just now. Please try again.",
};

function useGoogleError() {
  const [code] = useState(() => new URLSearchParams(window.location.search).get("google_error"));
  return code ? googleErrors[code] ?? googleErrors.failed : null;
}

function useAuthOptions() {
  return useQuery({ queryKey: ["auth-options"], queryFn: () => api<{ google: boolean }>("/v1/auth/options"), staleTime: Infinity, retry: false });
}

function GoogleButton({ intent, invite }: { intent: "login" | "invite"; invite?: string }) {
  const options = useAuthOptions();
  const [busy, setBusy] = useState(false);
  // Coming back with the browser's Back button restores the page as it was left.
  useEffect(() => {
    const shown = (e: PageTransitionEvent) => { if (e.persisted) setBusy(false); };
    window.addEventListener("pageshow", shown);
    return () => window.removeEventListener("pageshow", shown);
  }, []);
  if (!options.data?.google) return null;
  return (
    <>
      <button type="button" className="secondary wide-button" disabled={busy} onClick={() => {
        setBusy(true);
        // If the browser doesn't leave the page (blocked, or Back pressed), let them try again.
        setTimeout(() => setBusy(false), 8000);
        startGoogle(intent, invite);
      }}>
        {busy ? "Opening Google…" : "Continue with Google"}
      </button>
      <p className="divider"><span>or</span></p>
    </>
  );
}

// The API sends the browser here after Google, with a one-time ticket.
/** SupportDone is where the admin console sends SVENEX support with a one-time ticket. */
export function SupportDone() {
  const [ticket] = useState(() => window.location.hash.slice(1));
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  useEffect(() => {
    history.replaceState(null, "", "/support");
    if (!ticket) {
      navigate("/login", { replace: true });
      return;
    }
    completeSupport(ticket).then(() => navigate("/", { replace: true }), setError);
  }, [ticket, navigate]);
  if (!error) return <div className="auth"><Loading /></div>;
  return (
    <div className="auth">
      <h1>Couldn't open their SVENEX</h1>
      <ErrorText error={error} />
      <p className="muted small">Start it again from the admin console, with a new code if needed.</p>
    </div>
  );
}

export function GoogleDone() {
  const [ticket] = useState(() => window.location.hash.slice(1));
  const [error, setError] = useState<unknown>(null);
  const [challenge, setChallenge] = useState<CodeChallenge | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    history.replaceState(null, "", "/auth/google");
    if (!ticket) {
      navigate("/login", { replace: true });
      return;
    }
    completeGoogle(ticket).then((ch) => (ch ? setChallenge(ch) : navigate("/", { replace: true })), setError);
  }, [ticket, navigate]);
  if (challenge) return <CodeStep challenge={challenge} onBack={() => navigate("/login")} onDone={() => navigate("/", { replace: true })} />;
  if (!error) return <div className="auth"><Loading /></div>;
  return (
    <div className="auth">
      <h1>Couldn't sign you in</h1>
      <ErrorText error={error} />
      <Link className="button" to="/login">Back to sign in</Link>
    </div>
  );
}

export function Login() {
  const { status } = useSession();
  const navigate = useNavigate();
  const googleError = useGoogleError();
  const [challenge, setChallenge] = useState<CodeChallenge | null>(null);
  if (status === "signed_in") return <Navigate to="/" replace />;
  if (challenge) return <CodeStep challenge={challenge} onBack={() => setChallenge(null)} onDone={() => navigate("/")} />;
  const idle = Number(new URLSearchParams(window.location.search).get("idle") || 0);
  return (
    <div className="auth">
      <h1>Sign in to SVENEX</h1>
      {idle > 0 && <p className="notice small">You were signed out after {idle >= 60 ? `${Math.round(idle / 60)} hour${idle >= 120 ? "s" : ""}` : `${idle} minutes`} without activity, as your company asks.</p>}
      {googleError && <p className="error">{googleError}</p>}
      <GoogleButton intent="login" />
      <Form
        submitLabel="Sign in"
        onSubmit={async (d) => {
          const c = await login(str(d, "email"), String(d.get("password")));
          if (c) setChallenge(c);
          else navigate("/");
        }}
      >
        <Field label="Email">
          <input name="email" type="email" autoComplete="username" required />
        </Field>
        <Field label="Password">
          <input name="password" type="password" autoComplete="current-password" required />
        </Field>
      </Form>
      <p className="auth-alt"><Link className="button-link" to="/forgot">Forgot your password?</Link></p>
    </div>
  );
}

// Resend is allowed again after 30 seconds (the server enforces the same).
function useCooldown(seconds: number) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, () => setLeft(seconds)] as const;
}

function ResendButton({ challenge, purpose }: { challenge: CodeChallenge; purpose: "login" | "password_reset" }) {
  const [left, restart] = useCooldown(30);
  const [note, setNote] = useState<unknown>(null);
  return (
    <p className="small">
      {left > 0 ? (
        <span className="muted">Didn't get it? You can resend in {left}s.</span>
      ) : (
        <button type="button" className="link" onClick={async () => {
          setNote(null);
          try {
            await resendCode(challenge.challenge_token, purpose);
            restart();
          } catch (err) {
            setNote(err);
          }
        }}>Send a new code</button>
      )}
      <ErrorText error={note} />
    </p>
  );
}

function CodeStep({ challenge, onBack, onDone }: { challenge: CodeChallenge; onBack: () => void; onDone: () => void }) {
  const [remember, setRemember] = useState(true);
  if (challenge.method === "totp") {
    return (
      <div className="auth">
        <h1>Enter your app code</h1>
        <p className="muted">Open your authenticator app and enter the 6-digit code for SVENEX. Lost your phone? Enter one of your recovery codes instead.</p>
        <Form submitLabel="Continue" onSubmit={async (d) => {
          await verifyCode(challenge.challenge_token, str(d, "code"), false);
          onDone();
        }}>
          <Field label="Code">
            <input name="code" autoComplete="one-time-code" maxLength={12} required autoFocus spellCheck={false} />
          </Field>
        </Form>
        <button className="link small" onClick={onBack}>Use a different account</button>
      </div>
    );
  }
  return (
    <div className="auth">
      <h1>Check your email</h1>
      <p className="muted">We sent a 6-digit code to {challenge.email_hint}. It expires in 10 minutes.</p>
      <Form
        submitLabel="Continue"
        onSubmit={async (d) => {
          await verifyCode(challenge.challenge_token, str(d, "code"), remember && challenge.remember_allowed);
          onDone();
        }}
      >
        <Field label="Code">
          <input name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus />
        </Field>
        {challenge.remember_allowed ? (
          <label className="inline small"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Don't ask again on this device for 30 days</label>
        ) : (
          <p className="muted small">Operator accounts need a code every time.</p>
        )}
      </Form>
      <ResendButton challenge={challenge} purpose="login" />
      <button className="link small" onClick={onBack}>Use a different account</button>
    </div>
  );
}

export function Forgot() {
  const [challenge, setChallenge] = useState<CodeChallenge | null>(null);
  const [done, setDone] = useState(false);
  if (done) {
    return (
      <div className="auth">
        <h1>Password updated</h1>
        <p>You've been signed out everywhere. Sign in with your new password.</p>
        <Link className="button" to="/login">Sign in</Link>
      </div>
    );
  }
  if (!challenge) {
    return (
      <div className="auth">
        <h1>Reset your password</h1>
        <p className="muted">Enter your account email and we'll send you a code.</p>
        <Form submitLabel="Send code" onSubmit={async (d) => {
          const r = await api<{ challenge: CodeChallenge }>("/v1/auth/password/forgot", { method: "POST", json: { email: str(d, "email") } });
          setChallenge(r.challenge);
        }}>
          <Field label="Email"><input name="email" type="email" autoComplete="username" required /></Field>
        </Form>
        <p className="auth-alt"><Link className="button-link" to="/login">Back to sign in</Link></p>
      </div>
    );
  }
  return (
    <div className="auth">
      <h1>Check your email</h1>
      <p className="muted">If {challenge.email_hint} has a SVENEX account, we sent it a 6-digit code.</p>
      <Form submitLabel="Set new password" onSubmit={async (d) => {
        await api("/v1/auth/password/reset", { method: "POST", json: { challenge_token: challenge.challenge_token, code: str(d, "code"), new_password: String(d.get("password")) } });
        setDone(true);
      }}>
        <Field label="Code"><input name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} required autoFocus /></Field>
        <Field label="New password (10+ characters)"><input name="password" type="password" autoComplete="new-password" minLength={10} required /></Field>
      </Form>
      <ResendButton challenge={challenge} purpose="password_reset" />
    </div>
  );
}

// The invite token is in the URL fragment so it never reaches server logs.
export function Invite() {
  const [token] = useState(() => window.location.hash.slice(1));
  const googleError = useGoogleError();
  const [challenge, setChallenge] = useState<CodeChallenge | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (token) history.replaceState(null, "", "/invite");
  }, [token]);
  const preview = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api<{ organization_name: string; email: string; account_exists: boolean }>("/v1/auth/invites/preview", { method: "POST", json: { token } }),
    enabled: !!token,
    retry: false,
  });
  if (!token) return <div className="auth"><p>This invite link is incomplete. Open the full link you were sent.</p></div>;
  if (preview.isLoading) return <Loading />;
  if (preview.error) return <div className="auth"><ErrorText error={preview.error} /><Link to="/login">Go to sign in</Link></div>;
  const p = preview.data!;
  if (challenge) return <CodeStep challenge={challenge} onBack={() => navigate("/login")} onDone={() => navigate("/")} />;
  return (
    <div className="auth">
      <h1>Join {p.organization_name}</h1>
      <p className="muted">Invitation for {p.email}</p>
      {googleError && <p className="error">{googleError}</p>}
      <GoogleButton intent="invite" invite={token} />
      <Form
        submitLabel={p.account_exists ? "Sign in and join" : "Create account and join"}
        onSubmit={async (d) => {
          const c = await acceptInvite(token, str(d, "display_name"), String(d.get("password")));
          if (c) setChallenge(c);
          else navigate("/");
        }}
      >
        {!p.account_exists && (
          <Field label="Your name">
            <input name="display_name" required maxLength={120} autoComplete="name" />
          </Field>
        )}
        <Field label={p.account_exists ? "Your SVENEX password" : "Choose a password (10+ characters)"}>
          <input name="password" type="password" required minLength={p.account_exists ? 1 : 10} autoComplete={p.account_exists ? "current-password" : "new-password"} />
        </Field>
      </Form>
    </div>
  );
}

// Home sends people straight to their only org, or lets them choose.
export function Home() {
  const me = useMe();
  if (me.isLoading) return <Loading />;
  if (me.error) return <ErrorText error={me.error} />;
  if (me.data!.deletion_scheduled_for) return <AccountLeaving when={me.data!.deletion_scheduled_for} />;
  const orgs = me.data!.organizations;
  if (orgs.length === 1) return <Navigate to={`/o/${orgs[0].id}`} replace />;
  return (
    <div className="auth wide">
      <h1>Choose a company</h1>
      {orgs.length === 0 && <p className="muted">You're not a member of any organization yet. Ask your administrator for an invite.</p>}
      <div className="org-picker">
        {orgs.map((o) => (
          <Link key={o.id} className="org-row" to={`/o/${o.id}`}>
            <span className="brand-mark" aria-hidden>{o.display_name.trim().charAt(0).toUpperCase()}</span>
            <span className="org-row-text"><b>{o.display_name}</b>
              <small>{o.status === "closed" ? "Closed: open it to reopen" : <>{o.crm_role ? roleLabel[o.crm_role] : "No CRM access"}{!o.crm_active && " · subscription inactive"}</>}</small>
            </span>
            <span className="org-row-go" aria-hidden><Icon name="chevron" /></span>
          </Link>
        ))}
      </div>
      <button className="link" onClick={() => logout()}>Sign out</button>
    </div>
  );
}
